import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import {
  bindDamageFieldTexture,
  setDamageFieldTime,
} from '../../materials/core/DamageFieldBinding';
import {
  clearDecalStampUniforms,
  ensureDecalAtlasLoaded,
  isDecalAtlasReady,
  setDecalWallOrigin,
  writeDecalStampUniforms,
} from '../../materials/core/DamageDecalAtlas';
import {
  stampAtlasRow,
  STAMP_ENERGY_CALIBRATION_J,
  STAMP_ENERGY_CALIBRATION_WOOD_J,
} from './damageDecalStamps';
import {
  createDecalStampRing,
  packLiveDecalStamps,
  resetDecalStampRing,
  retireBakeDownBatch,
  selectBakeDownBatch,
} from './decalStampRing';
import { bakeDownStamps } from '../../materials/core/DamageBakeDown';
import type { DemolitionBakeHandle } from '../../materials/core/DemolitionBakedMaterials';
import {
  createDamageField,
  stampDamageField,
  type DamageField,
} from '../../structures/demolition/demolitionDamageField';
import type { DamageFact } from '../../structures/demolition/demolitionDamageLedger';
import { cellTransformOffset, type DemolitionRuntime } from '../../structures/demolition/demolitionPhysics';
import type { MaterialAppearanceDescriptor } from '../../materials/appearanceDescriptor';
import { getAppearanceDescriptor, NEUTRAL_APPEARANCE_DESCRIPTOR } from '../../materials/MaterialRegistry';
import {
  createDamageFieldTexture,
  DamageFieldClock,
  isDamageFieldSamplingEnabled,
  markDamageFieldTextureDirty,
  uploadDamageFieldTextureIfDirty,
} from './damageFieldTexture';
import type { DemolitionEffectTier } from './effectTier';

interface FactVisual {
  root: THREE.Group;
  fact: DamageFact;
  restSurfacePosition: THREE.Vector3;
  restNormal: THREE.Vector3;
  restQuaternion: THREE.Quaternion;
  worldPosition: THREE.Vector3;
  worldNormal: THREE.Vector3;
  bodyPosition: THREE.Vector3;
  bodyQuaternion: THREE.Quaternion;
  localPosition: THREE.Vector3;
  attachedCellId?: number;
  materials: THREE.Material[];
}

const FRONT = new THREE.Vector3(0, 0, 1);

function hash01(seed: number): number {
  let h = Math.imul(seed | 0, 0x9e3779b1) ^ 0x6d2b79f5;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  return ((h ^ (h >>> 16)) >>> 0) / 0x1_0000_0000;
}

function factSeverity(fact: DamageFact): number {
  return Math.max(fact.normalizedSeverity, fact.damageAfter * 0.58);
}

/**
 * Visual crater radius. `fact.radius` is the energy kernel (cannon = 1.15 m),
 * not the hole; cap it so the annulus sits on the surviving rim.
 */
export function visualCarveRadius(fact: DamageFact): number {
  const severity = factSeverity(fact);
  const cap = fact.kind === 'blast' ? 1.35 : 0.28 + severity * 0.30;
  return Math.max(0.14, Math.min(fact.radius, cap));
}

function tangentBasis(normal: THREE.Vector3): { t1: THREE.Vector3; t2: THREE.Vector3 } {
  const helper = Math.abs(normal.y) < 0.92
    ? new THREE.Vector3(0, 1, 0)
    : new THREE.Vector3(1, 0, 0);
  const t1 = new THREE.Vector3().crossVectors(normal, helper).normalize();
  const t2 = new THREE.Vector3().crossVectors(normal, t1).normalize();
  return { t1, t2 };
}

function offsetOnFace(
  center: THREE.Vector3,
  t1: THREE.Vector3,
  t2: THREE.Vector3,
  angle: number,
  radius: number,
): { x: number; y: number; z: number } {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return {
    x: center.x + t1.x * c * radius + t2.x * s * radius,
    y: center.y + t1.y * c * radius + t2.y * s * radius,
    z: center.z + t1.z * c * radius + t2.z * s * radius,
  };
}

/** Compose disc stamps. Topology facts get an annulus on the surviving rim. */
export function stampFactDamage(
  field: DamageField,
  fact: DamageFact,
  restPosition: THREE.Vector3,
  restNormal: THREE.Vector3,
  clock: DamageFieldClock = new DamageFieldClock(),
): number {
  const severity = factSeverity(fact);
  const age = clock.normalize(fact.simTime);
  const seed = hash01(fact.sequence);
  if (!fact.topologyChanged) {
    const stampRadius = Math.min(
      fact.radius,
      0.16 + fact.normalizedSeverity * 0.28 + fact.damageAfter * 0.12,
    );
    const scuffDamage = Math.max(fact.damageAfter, fact.normalizedSeverity * 0.45);
    let touched = stampDamageField(field, {
      position: restPosition,
      radius: stampRadius,
      damage: scuffDamage,
      age,
      seed,
    });
    const coreRadius = stampRadius * 0.35;
    const coreDamage = Math.min(1, scuffDamage * 0.55 + 0.40 + severity * 0.2);
    if (coreRadius > 0.04 && coreDamage > scuffDamage + 0.04) {
      touched += stampDamageField(field, {
        position: restPosition,
        radius: coreRadius,
        damage: coreDamage,
        age,
        seed,
      });
    }
    return touched;
  }

  const carve = visualCarveRadius(fact);
  const ringWidth = 0.35 + severity * 0.25;
  const innerR = carve;
  const outerR = carve + ringWidth;
  const midR = (innerR + outerR) * 0.5;
  const discR = (outerR - innerR) * 0.55;
  const { t1, t2 } = tangentBasis(restNormal);
  const count = 8;
  const yaw = hash01(fact.sequence * 17) * 0.35;
  const scuffDamage = 0.60 + severity * 0.12;
  const coreDamage = 0.78 + severity * 0.18;
  let touched = 0;
  for (let i = 0; i < count; i += 1) {
    const angle = (i / count) * Math.PI * 2 + yaw;
    const jitter = 0.92 + hash01(fact.sequence * 31 + i) * 0.16;
    touched += stampDamageField(field, {
      position: offsetOnFace(restPosition, t1, t2, angle, midR * jitter),
      radius: discR,
      damage: scuffDamage,
      age,
      seed,
    });
  }
  const innerMid = innerR + discR * 0.22;
  const innerDisc = discR * 0.42;
  for (let i = 0; i < count; i += 1) {
    const angle = (i / count) * Math.PI * 2 + yaw + 0.18;
    touched += stampDamageField(field, {
      position: offsetOnFace(restPosition, t1, t2, angle, innerMid),
      radius: innerDisc,
      damage: coreDamage,
      age,
      seed,
    });
  }
  return touched;
}

function disposeObject(object: THREE.Object3D) {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(material)) material.forEach((item) => item.dispose());
    else material?.dispose();
  });
}

function clampToHalf(value: number, half: number): number {
  return Math.min(half, Math.max(-half, value));
}

/** Project an impact point onto the authored exterior face in wall rest space. */
function restSurfacePosition(
  fact: DamageFact,
  half: { x: number; y: number; z: number },
): THREE.Vector3 {
  const point = new THREE.Vector3(
    clampToHalf(fact.position.x, half.x),
    clampToHalf(fact.position.y, half.y),
    clampToHalf(fact.position.z, half.z),
  );
  // Snap to the nearest AABB face of the *position*, not the incoming-velocity
  // axis. Grazing shots store outwardNormal = −incoming, which is mostly
  // tangent, and used to park the stamp on a side/top face — invisible from
  // the front camera.
  const dx = half.x - Math.abs(point.x);
  const dy = half.y - Math.abs(point.y);
  const dz = half.z - Math.abs(point.z);
  if (dz <= dx && dz <= dy) point.z = (point.z < 0 ? -1 : 1) * half.z;
  else if (dx <= dy) point.x = (point.x < 0 ? -1 : 1) * half.x;
  else point.y = (point.y < 0 ? -1 : 1) * half.y;
  return point;
}

/** Rest-space chips only. Surface bruise/cracks live in the damage-field shader. */
export function buildFactVisual(
  fact: DamageFact,
  wallHalf: { x: number; y: number; z: number },
  appearance: Pick<MaterialAppearanceDescriptor, 'fragment'> = NEUTRAL_APPEARANCE_DESCRIPTOR,
): FactVisual {
  const root = new THREE.Group();
  const normal = new THREE.Vector3(fact.outwardNormal.x, fact.outwardNormal.y, fact.outwardNormal.z).normalize();
  const restQuaternion = new THREE.Quaternion().setFromUnitVectors(FRONT, normal);

  const topologyCue = fact.topologyChanged;
  const severity = factSeverity(fact);
  const radius = topologyCue
    ? visualCarveRadius(fact)
    : Math.min(0.52, Math.max(0.08, fact.radius * (0.12 + severity * 0.18)));

  const materials: THREE.Material[] = [];
  // Sparse aggregate-tinted rim debris only — no bright confetti at gameplay distance.
  const chipCount = topologyCue
    ? Math.round(1 + severity * 2)
    : fact.damageAfter > 0.55 && fact.kind === 'blast' ? 2 : 0;
  const ringWidth = 0.35 + severity * 0.25;
  if (chipCount > 0) {
    const chipMaterial = new THREE.MeshBasicMaterial({
      color: appearance.fragment.outer ?? '#6a6560',
      transparent: true,
      opacity: 0.42 + severity * 0.12,
      depthWrite: false,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    // Aggregate-dark, never pale interior white.
    chipMaterial.color.multiplyScalar(0.55);
    materials.push(chipMaterial);
    for (let i = 0; i < chipCount; i += 1) {
      const angle = hash01(fact.sequence * 73 + i) * Math.PI * 2;
      const distance = topologyCue
        ? radius + hash01(fact.sequence * 91 + i) * ringWidth * 0.55
        : radius * Math.sqrt(0.08 + hash01(fact.sequence * 79 + i) * 0.7);
      const size = 0.006 + hash01(fact.sequence * 53 + i) * 0.01;
      const chip = new THREE.Mesh(new THREE.IcosahedronGeometry(size, 0), chipMaterial);
      chip.position.set(Math.cos(angle) * distance, Math.sin(angle) * distance, 0.012 + i * 0.0003);
      chip.rotation.set(hash01(i + 11), hash01(i + 19), hash01(i + 23));
      root.add(chip);
    }
  }

  return {
    root,
    fact,
    restSurfacePosition: restSurfacePosition(fact, wallHalf),
    restNormal: normal,
    restQuaternion,
    worldPosition: new THREE.Vector3(),
    worldNormal: new THREE.Vector3(),
    bodyPosition: new THREE.Vector3(),
    bodyQuaternion: new THREE.Quaternion(),
    localPosition: new THREE.Vector3(),
    materials,
  };
}

function findDynamicAttachment(runtime: DemolitionRuntime, visual: FactVisual): number | undefined {
  const preferred = visual.fact.cellId;
  if (preferred !== undefined && runtime.records.get(preferred)?.state === 'dynamic') {
    return preferred;
  }

  let bestId: number | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  let nearestId: number | undefined;
  let nearestDistance = Number.POSITIVE_INFINITY;
  const point = visual.restSurfacePosition;
  for (const cell of runtime.cells) {
    const record = runtime.records.get(cell.id);
    if (!record || record.state !== 'dynamic') continue;
    const dx = cell.centroid.x - point.x;
    const dy = cell.centroid.y - point.y;
    const dz = cell.centroid.z - point.z;
    const distance = dx * dx + dy * dy + dz * dz;
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestId = cell.id;
    }
    const inside =
      point.x >= cell.aabb.min.x - 0.04 && point.x <= cell.aabb.max.x + 0.04 &&
      point.y >= cell.aabb.min.y - 0.04 && point.y <= cell.aabb.max.y + 0.04 &&
      point.z >= cell.aabb.min.z - 0.04 && point.z <= cell.aabb.max.z + 0.04;
    if (!inside) continue;
    if (distance < bestDistance) {
      bestDistance = distance;
      bestId = cell.id;
    }
  }
  return bestId ?? (nearestDistance < 1.1 ? nearestId : undefined);
}

function nearestStaticCentroidDistSq(
  runtime: DemolitionRuntime,
  point: { x: number; y: number; z: number },
): number {
  let best = Number.POSITIVE_INFINITY;
  for (const cell of runtime.cells) {
    const record = runtime.records.get(cell.id);
    if (!record || record.state !== 'static') continue;
    const dx = cell.centroid.x - point.x;
    const dy = cell.centroid.y - point.y;
    const dz = cell.centroid.z - point.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq < best) best = distSq;
  }
  return best;
}

function hideChipsOffRemainingWall(runtime: DemolitionRuntime, visual: FactVisual): void {
  const maxDistSq = 0.38 * 0.38;
  const offset = new THREE.Vector3();
  const rest = new THREE.Vector3();
  for (const child of visual.root.children) {
    offset.copy(child.position).applyQuaternion(visual.restQuaternion);
    rest.copy(visual.restSurfacePosition).add(offset);
    child.visible = nearestStaticCentroidDistSq(runtime, rest) <= maxDistSq;
  }
}

function placeFactVisual(runtime: DemolitionRuntime, visual: FactVisual): boolean {
  if (runtime.fractured && visual.attachedCellId === undefined && !visual.fact.topologyChanged) {
    visual.attachedCellId = findDynamicAttachment(runtime, visual);
  }
  const attachedCellId = visual.attachedCellId;
  const record = attachedCellId === undefined ? undefined : runtime.records.get(attachedCellId);
  const cell = attachedCellId === undefined
    ? undefined
    : runtime.cellById.get(attachedCellId) ?? runtime.pristineCellById.get(attachedCellId);
  const transformOffset =
    record?.state === 'dynamic' && attachedCellId !== undefined
      ? cellTransformOffset(runtime, attachedCellId)
      : null;

  if (cell && transformOffset !== null) {
    const transforms = runtime.transforms;
    visual.bodyPosition.set(
      transforms[transformOffset],
      transforms[transformOffset + 1],
      transforms[transformOffset + 2],
    );
    visual.bodyQuaternion.set(
      transforms[transformOffset + 3],
      transforms[transformOffset + 4],
      transforms[transformOffset + 5],
      transforms[transformOffset + 6],
    );
    visual.localPosition.copy(visual.restSurfacePosition).sub(cell.centroid);
    visual.worldPosition.copy(visual.localPosition).applyQuaternion(visual.bodyQuaternion).add(visual.bodyPosition);
    visual.worldNormal.copy(visual.restNormal).applyQuaternion(visual.bodyQuaternion).normalize();
    visual.root.position.copy(visual.worldPosition).addScaledVector(visual.worldNormal, 0.014);
    visual.root.quaternion.copy(visual.bodyQuaternion).multiply(visual.restQuaternion);
    return true;
  }

  if (record && record.state === 'removed') return false;

  visual.worldPosition.copy(runtime.wallCenter).add(visual.restSurfacePosition);
  visual.worldNormal.copy(visual.restNormal).normalize();
  visual.root.position.copy(visual.worldPosition).addScaledVector(visual.worldNormal, 0.014);
  visual.root.quaternion.copy(visual.restQuaternion);
  if (visual.fact.topologyChanged && runtime.fractured) {
    hideChipsOffRemainingWall(runtime, visual);
  }
  return true;
}

/**
 * Phase-1 durable surface read. The shader owns bruise/cracks via the stamped
 * RGBA8 field. This layer only places physical icosahedron chips and keeps them
 * attached in wall rest space (including on released rotating chunks).
 */
export interface BakeDownInfo {
  ms: number;
  live: number;
  bakedDown: number;
}

export function Phase1DamageLayer({
  runtime,
  tier,
  bakeHandle = null,
  onBakeDown,
}: {
  runtime: DemolitionRuntime;
  tier: DemolitionEffectTier;
  bakeHandle?: DemolitionBakeHandle | null;
  onBakeDown?: (info: BakeDownInfo) => void;
}) {
  const { gl } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const visuals = useRef<Map<number, FactVisual>>(new Map());
  const fieldRef = useRef<DamageField | null>(null);
  const fieldTextureRef = useRef<THREE.Data3DTexture | null>(null);
  const clockRef = useRef(new DamageFieldClock());
  const stampRingRef = useRef(createDecalStampRing());
  const lastFactCountRef = useRef(0);
  const pendingBakeDownRef = useRef(false);
  const bakeHandleRef = useRef(bakeHandle);
  bakeHandleRef.current = bakeHandle;
  const onBakeDownRef = useRef(onBakeDown);
  onBakeDownRef.current = onBakeDown;
  const restLookup = (fact: DamageFact) => {
    const visual = visuals.current.get(fact.sequence);
    return visual?.restSurfacePosition
      ?? new THREE.Vector3(fact.position.x, fact.position.y, fact.position.z);
  };
  const calibrationOf = () => (
    runtime.profile.id === 'wood'
      ? STAMP_ENERGY_CALIBRATION_WOOD_J
      : STAMP_ENERGY_CALIBRATION_J
  );

  const runBakeDownRef = useRef<(force: boolean) => BakeDownInfo>(() => ({
    ms: 0,
    live: 0,
    bakedDown: 0,
  }));
  const runBakeDown = (force: boolean): BakeDownInfo => {
    const ring = stampRingRef.current;
    const facts = runtime.damageLedger.facts;
    const clock = clockRef.current;
    const handle = bakeHandleRef.current;
    const empty = { ms: 0, live: ring.packed.count, bakedDown: ring.bakedDown };
    if (!handle?.atlas || !handle.outer) return empty;
    const batch = selectBakeDownBatch(
      facts,
      ring,
      clock,
      restLookup,
      calibrationOf(),
      force ? 'all' : 'auto',
    );
    if (!batch) return empty;
    const result = bakeDownStamps(gl, {
      atlas: handle.atlas,
      material: handle.outer,
      wall: handle.wall,
    }, batch.packed);
    if (result.faces.length === 0) return empty;
    handle.lastBakeDownMs = result.ms;
    retireBakeDownBatch(ring, batch);
    packLiveDecalStamps(ring, facts, clock, restLookup, calibrationOf());
    writeDecalStampUniforms(ring.packed);
    const info = {
      ms: result.ms,
      live: ring.packed.count,
      bakedDown: ring.bakedDown,
    };
    onBakeDownRef.current?.(info);
    void force;
    return info;
  };
  runBakeDownRef.current = runBakeDown;

  const enabled = tier !== 'off';
  const samplingEnabled = isDamageFieldSamplingEnabled();
  const field = useMemo(
    () => enabled
      ? createDamageField({ x: runtime.wallHalf.x, y: runtime.wallHalf.y, z: runtime.wallHalf.z })
      : null,
    [enabled, runtime],
  );

  useEffect(() => {
    fieldRef.current = field;
    clockRef.current = new DamageFieldClock();
    return () => {
      fieldRef.current = null;
    };
  }, [field]);

  useEffect(() => {
    void ensureDecalAtlasLoaded();
  }, []);

  useEffect(() => {
    if (!field || !samplingEnabled) {
      bindDamageFieldTexture(null);
      fieldTextureRef.current = null;
      return;
    }
    const texture = createDamageFieldTexture(field);
    fieldTextureRef.current = texture;
    bindDamageFieldTexture(texture, field.halfExtents);
    return () => {
      bindDamageFieldTexture(null);
      if (fieldTextureRef.current === texture) fieldTextureRef.current = null;
      texture.dispose();
    };
  }, [field, samplingEnabled]);

  useFrame(() => {
    const group = groupRef.current;
    if (!group || !enabled) return;

    const clock = clockRef.current;
    const simTime = runtime.stepper.simTime;
    if (fieldRef.current) {
      clock.maybeRebase(fieldRef.current, fieldTextureRef.current, simTime);
    }
    setDamageFieldTime(clock.normalize(simTime));
    if (import.meta.env.DEV) {
      const exposed = window as Window & {
        __damageFieldClock?: { epoch: number; normalized: number };
      };
      exposed.__damageFieldClock = {
        epoch: clock.epoch,
        normalized: clock.normalize(simTime),
      };
    }

    const facts = runtime.damageLedger.facts;
    runtime.decalAtlasReady = isDecalAtlasReady();
    runtime.decalStampCount = stampRingRef.current.packed.count;
    runtime.decalBakedDownCount = stampRingRef.current.bakedDown;
    if (import.meta.env.DEV) {
      const packed = stampRingRef.current.packed;
      runtime.decalStamps = Array.from({ length: packed.count }, (_, i) => {
        const energy = packed.b[i].x;
        const grazing = Math.min(1, Math.max(0, (packed.b[i].w % 1) / 0.99));
        return {
          x: packed.a[i].x,
          y: packed.a[i].y,
          z: packed.a[i].z,
          radius: packed.r[i],
          energy,
          grazing,
          row: stampAtlasRow(energy, grazing),
        };
      });
    }
    setDecalWallOrigin(runtime.wallCenter);
    const active = new Set(facts.map((fact) => fact.sequence));
    for (const [sequence, visual] of visuals.current) {
      if (active.has(sequence)) continue;
      group.remove(visual.root);
      disposeObject(visual.root);
      visuals.current.delete(sequence);
    }

    for (const fact of facts) {
      let visual = visuals.current.get(fact.sequence);
      if (!visual) {
        visual = buildFactVisual(
          fact,
          runtime.wallHalf,
          getAppearanceDescriptor(runtime.profile.id),
        );
        group.add(visual.root);
        visuals.current.set(fact.sequence, visual);
        if (fieldRef.current) {
          const touched = stampFactDamage(
            fieldRef.current,
            fact,
            visual.restSurfacePosition,
            visual.restNormal,
            clock,
          );
          if (touched > 0 && fieldTextureRef.current) {
            markDamageFieldTextureDirty(fieldTextureRef.current);
          }
        }
      }

      const age = Math.max(0, runtime.stepper.simTime - fact.simTime);
      visual.root.visible = placeFactVisual(runtime, visual);
      const fade = Math.max(0.58, 1 - age / 24);
      for (const material of visual.materials) {
        if ('opacity' in material) {
          const base = material.userData.phase1BaseOpacity as number | undefined;
          if (base === undefined) material.userData.phase1BaseOpacity = material.opacity;
          material.opacity = (base ?? material.opacity) * fade;
        }
      }
    }

    if (facts.length === 0) {
      if (lastFactCountRef.current > 0) {
        const fieldData = fieldRef.current;
        if (fieldData) {
          fieldData.data.fill(0);
          if (fieldTextureRef.current) markDamageFieldTextureDirty(fieldTextureRef.current);
        }
        const handle = bakeHandleRef.current;
        if (handle && stampRingRef.current.bakedDown > 0) {
          handle.restorePristine(gl);
        }
        resetDecalStampRing(stampRingRef.current);
        clearDecalStampUniforms();
        pendingBakeDownRef.current = false;
      }
      lastFactCountRef.current = 0;
    } else {
      const ring = stampRingRef.current;
      const canBake = Boolean(bakeHandleRef.current?.atlas && bakeHandleRef.current.outer);
      packLiveDecalStamps(ring, facts, clock, restLookup, calibrationOf());
      if (canBake && runtime.bakeDownAuto !== false) {
        const batchReady = selectBakeDownBatch(facts, ring, clock, restLookup, calibrationOf());
        if (batchReady) {
          if (pendingBakeDownRef.current) {
            runBakeDown(false);
            pendingBakeDownRef.current = false;
            // Drain extra overflow in this frame so the live ring never
            // silently drops unbaked stamps.
            while (selectBakeDownBatch(facts, ring, clock, restLookup, calibrationOf())) {
              runBakeDown(false);
            }
          } else {
            pendingBakeDownRef.current = true;
          }
        } else {
          pendingBakeDownRef.current = false;
        }
      }
      writeDecalStampUniforms(ring.packed);
      lastFactCountRef.current = facts.length;
    }

    runtime.decalStampCount = stampRingRef.current.packed.count;
    runtime.decalBakedDownCount = stampRingRef.current.bakedDown;
    runtime.decalRing = {
      live: stampRingRef.current.packed.count,
      bakedDown: stampRingRef.current.bakedDown,
      pending: pendingBakeDownRef.current,
    };

    if (fieldTextureRef.current) uploadDamageFieldTextureIfDirty(fieldTextureRef.current);
  });

  useEffect(() => {
    runtime.forceBakeDown = () => {
      pendingBakeDownRef.current = false;
      const info = runBakeDownRef.current(true);
      runtime.decalStampCount = stampRingRef.current.packed.count;
      runtime.decalBakedDownCount = stampRingRef.current.bakedDown;
      runtime.decalRing = {
        live: stampRingRef.current.packed.count,
        bakedDown: stampRingRef.current.bakedDown,
        pending: false,
      };
      return info;
    };
    return () => {
      if (runtime.forceBakeDown) delete runtime.forceBakeDown;
    };
  }, [runtime]);

  useEffect(() => () => {
    for (const visual of visuals.current.values()) disposeObject(visual.root);
    visuals.current.clear();
    resetDecalStampRing(stampRingRef.current);
    clearDecalStampUniforms();
  }, []);

  if (!enabled) return null;
  return <group ref={groupRef} renderOrder={3} />;
}
